import React, { useEffect, useState, useMemo } from "react";
import MDAvatar from "components/MDAvatar";
import Axios from "axios";
import Cookies from "js-cookie";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddReactionIcon from "@mui/icons-material/AddReaction";
import { useCategories, useUpdateCategory, useDeleteCategory } from "api/useCategories.ts";

export default function categoriesTableData() {
  const { data: fetchedCategories, isLoading } = useCategories();
  const deleteCategory = useDeleteCategory();
  const updateCategory = useUpdateCategory();
  const [categories, setCategories] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isIcon, setIsIcon] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success", // "success" | "error"
  });

  const [editedCategory, setEditedCategory] = useState({
    _id: "",
    name: {
      en: "",
      ar: "",
    },
    description: {
      en: "",
      ar: "",
    },
  });

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImagePreview(URL.createObjectURL(e.target.files[0]));
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleOpenFileModal = (categoryId) => {
    setSelectedId(categoryId);
    setSelectedFile(null);
    setImagePreview(null);
    setIsFileModalOpen(true);
  };

  const closeFileModal = () => {
    setIsFileModalOpen(false);
    setSelectedFile(null);
    setImagePreview(null);
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      setNotification({
        open: true,
        message: "No file selected",
        severity: "error",
      });
      return;
    }

    setIsUploading(true);

    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Unauthorized access");
      }

      const response = await Axios.patch(
        `${process.env.REACT_APP_API_URL}/categories/update-${
          isIcon ? "icon" : "image"
        }/${selectedId}`,
        { photo: selectedFile },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotification({
        open: true,
        message: `${isIcon ? "Icon" : "Image"} uploaded successfully.`,
        severity: "success",
      });

      // Reset image preview on successful upload
      setImagePreview(null);
      setSelectedFile(null);
      closeFileModal();
    } catch (error) {
      console.error("Error uploading image:", error.response || error.message);
      setNotification({
        open: true,
        message: `Failed to upload ${isIcon ? "icon" : "image"}. Please try again.`,
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const openDeleteModal = (categoryId) => {
    setSelectedId(categoryId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleEditClick = (category) => {
    setEditedCategory({
      _id: category._id,
      name: category.name,
      description: category.description,
    });
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditedCategory({
      _id: "",
      name: {
        en: "",
        ar: "",
      },
      description: {
        en: "",
        ar: "",
      },
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const keys = name.split(".");

    setEditedCategory((prev) => {
      if (keys.length === 2) {
        return {
          ...prev,
          [keys[0]]: {
            ...(prev?.[keys[0]] || {}), // Ensure it exists
            [keys[1]]: value,
          },
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleDeleteCategory = async () => {
    if (!selectedId) return;

    deleteCategory.mutate(selectedId, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: "Category deleted successfully.",
          severity: "success",
        });
        closeDeleteModal();
      },
      onError: (error) => {
        console.error("Error deleting Category:", error);
        setNotification({
          open: true,
          message: "Failed to delete Category. Please try again.",
          severity: "error",
        });
      },
    });
  };

  // Handling Save Changes
  const handleSaveChanges = async () => {
    const originalCategory = categories.find((c) => c._id === editedCategory._id);

    const updatedFields = Object.keys(editedCategory).reduce((changes, key) => {
      if (editedCategory[key] !== originalCategory[key]) {
        changes[key] = editedCategory[key];
      }
      return changes;
    }, {});

    if (Object.keys(updatedFields).length === 0) {
      setNotification({
        open: true,
        message: "No changes detected.",
        severity: "info",
      });
      return;
    }

    // Updated Category Data
    const updatedCategory = {
      _id: editedCategory._id,
      name: editedCategory.name,
      description: editedCategory.description,
    };

    updateCategory.mutate(updatedCategory, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: "Category updated successfully.",
          severity: "success",
        });
        handleCloseModal();
      },
      onError: (error) => {
        console.error("Error updating Category:", error);
        setNotification({
          open: true,
          message: "Failed to update Category. Please try again.",
          severity: "error",
        });
      },
    });
  };

  useEffect(() => {
    !isLoading && setCategories(fetchedCategories);
  }, [fetchedCategories, isLoading]);

  const rows = useMemo(() => {
    return isLoading
      ? Array(10)
          .fill(0)
          .map((_, index) => ({
            categoryId: index + 1,
            categoryName: "Loading...",
            categoryImg: <MDAvatar size="xl" />,
            categoryIcon: <MDAvatar size="xl" />,
            description: "Loading...",
            action: <div className="flex justify-center space-x-2">Loading...</div>,
          }))
      : Array.isArray(categories) &&
          categories.map((category) => ({
            categoryId: category._id,
            categoryName: (
              <div className="max-w-[100px] truncate">
                <p className="font-semibold" title={category.name.en || category.name.ar}>
                  {category.name.en || category.name.ar}
                </p>
              </div>
            ),
            categoryImg: (
              <MDAvatar
                src={category.image || "https://via.placeholder.com/150"}
                name={category.name.en || category.name.ar}
                size="xl"
              />
            ),
            categoryIcon: (
              <MDAvatar
                src={category.icon || "https://via.placeholder.com/150"}
                name={category.name.en || category.name.ar}
                size="xl"
              />
            ),
            description: category.description.en || category.description.ar,
            releaseDate: new Date(category.createdAt).toLocaleDateString(),

            action: (
              <div className="flex justify-center space-x-2">
                <IconButton color="primary" size="medium" onClick={() => handleEditClick(category)}>
                  <EditIcon fontSize="medium" />
                </IconButton>
                <IconButton
                  color="secondary"
                  size="medium"
                  onClick={() => {
                    setIsIcon(false);
                    handleOpenFileModal(category._id);
                  }}
                >
                  <AddPhotoAlternateIcon fontSize="medium" />
                </IconButton>
                <IconButton
                  color="secondary"
                  size="medium"
                  onClick={() => {
                    setIsIcon(true);
                    handleOpenFileModal(category._id);
                  }}
                >
                  <AddReactionIcon fontSize="medium" />
                </IconButton>
                <IconButton
                  color="error"
                  size="medium"
                  onClick={() => openDeleteModal(category._id)}
                >
                  <DeleteIcon fontSize="medium" />
                </IconButton>
              </div>
            ),
          }));
  }, [isLoading, categories]);

  return {
    columns: [
      { Header: "ID", accessor: "categoryId", align: "center" },
      { Header: "Category Name", accessor: "categoryName", align: "center" },
      { Header: "Category Image", accessor: "categoryImg", align: "center" },
      { Header: "Category Icon", accessor: "categoryIcon", align: "center" },
      { Header: "Description", accessor: "description", align: "center" },
      { Header: "Release Date", accessor: "releaseDate", align: "center" },
      { Header: "Actions", accessor: "action", align: "center" },
    ],
    rows,
    editedCategory,
    handleCloseModal,
    isModalOpen,
    notification,
    setNotification,
    handleSaveChanges,
    handleCloseNotification,
    handleInputChange,
    handleDeleteCategory,
    isDeleteModalOpen,
    closeDeleteModal,
    handleUploadImage,
    handleFileChange,
    isFileModalOpen,
    closeFileModal,
    imagePreview,
    setImagePreview,
    isIcon,
    isUploading,
  };
}
